/**
 * Adapter between the temporal grid's row shape and the weekly KPI engine.
 *
 * The grid stores one row per (metric_name, fiscal_year_name, producer_type)
 * with W01..W53 columns; the engine works on an ordered array of per-week
 * metric maps. `applyCellChangesLocally` is the client-side counterpart of
 * the server's `update_temporal_grid_data` (stateless recalculation), for a
 * single edit and a paste alike.
 *
 * A month, quarter, half-year, PTG or TTL edit is spread over its weeks as the
 * server's `roll_down_value` does (specs/domain/kpi-definitions.md § Period
 * entry spread), so the period shows what was typed. A cleared cell is 0. The
 * stock roll opens on the first edited week's own BOP.
 *
 * A cell the planner can correct — unreadable input, a negative FPOS, a KPI
 * the engine has no calculation for, a week before `forecastWeekFrom` or one
 * the grid holds no value for — is refused: the batch throws
 * `CellEditsRefused` listing every such cell and applies none. A frame missing
 * a flow input, or metadata not yet loaded, throws a plain error.
 *
 * idx_from nuance: the server recalculates from the first forecast week,
 * the client from the first edited week. On an engine-consistent grid (which
 * the server-rendered grid is) the results are identical — recomputing earlier
 * untouched weeks is a no-op and the roll opens on the edited week's own BOP,
 * which the edit does not change. The argument holds for edits at or after the
 * forecast boundary, which is why an earlier one throws.
 */

import { backingWeeks } from "../../components/cubeDataTable/utils/columnUtils";
import { type ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import { isPercentageMetric } from "../percentageMetrics";
import {
  type MetricValues,
  recalcDerivedMetrics
} from "../summary/editPropagation";
import { WEEKLY_SALES_DERIVATIONS } from "../summary/salesKpiDerivations";
import { WEEKLY_KEYS } from "../timePeriodUtils";
import {
  aggregate,
  type AggWeek,
  computeTemporalAggregates,
  fillWeeklyWeekCover,
  spreadsEveryWeek,
  temporalAggRule,
  TOTAL_LABEL
} from "./temporalAggregation";
import {
  applyIntakeSwap,
  applyWeeklyKpiChanges,
  EOP_UNITS,
  FPOS,
  FPOS_AT_COST,
  isWeeklyTrigger,
  potbFromOtb,
  WEEKLY_COUPLED_INPUT_METRICS,
  WEEKLY_FLOW_INPUT_METRICS
} from "./weeklyKpiEngine";

/** Structural view of a transactional grid row (identity fields + W01..W53). */
export type TemporalGridRow = Record<string, unknown>;

export interface TemporalCellChange {
  data: {
    metric_name: string;
    fiscal_year_name: string;
    producer_type: string;
  };
  colId: string;
  /** The cell's value before the edit; blank is 0. A period edit moves its weeks by value − oldValue (a ratio scales them by value / oldValue); a `last` period edit ignores it. */
  oldValue?: unknown;
  value: unknown;
}

export interface WeekSlot {
  fiscalYear: string;
  weekKey: string;
}

function rowString(row: TemporalGridRow, key: string): string {
  const value = row[key];
  return typeof value === "string" ? value : "";
}

export function buildSlots(
  rows: TemporalGridRow[],
  yearMapping: Record<string, number> | undefined
): WeekSlot[] {
  const years = [
    ...new Set(rows.map((row) => rowString(row, "fiscal_year_name")))
  ];
  years.sort(function byOffset(a, b) {
    const offsetDelta = (yearMapping?.[a] ?? 0) - (yearMapping?.[b] ?? 0);
    return offsetDelta !== 0 ? offsetDelta : a.localeCompare(b);
  });

  const slots: WeekSlot[] = [];
  for (const fiscalYear of years) {
    const yearRows = rows.filter(
      (row) => rowString(row, "fiscal_year_name") === fiscalYear
    );
    for (const weekKey of WEEKLY_KEYS) {
      if (yearRows.some((row) => row[weekKey] != null)) {
        slots.push({ fiscalYear, weekKey });
      }
    }
  }
  return slots;
}

/**
 * Client-side recalculation of one or more cell edits, applied in order as the
 * server applies them. Returns an AG Grid row transaction (rows matched by the
 * grid's fiscalYear_metric_producer row id) carrying the weekly cascade rolled
 * up into the M/Q/HY/PTG/TTL columns (server: sf_to_gf).
 *
 * `markdownStatus` is the applied context filter; it drives the sign of the
 * planned-markdown EOP correction in the roll-forward (see `markdownFlowSign`).
 */
export function applyCellChangesLocally(
  rows: TemporalGridRow[],
  cellChanges: TemporalCellChange[],
  metaData: ForecastMetaDataType | undefined,
  markdownStatus?: string[] | null,
  forecastWeekFrom?: number
): { update: TemporalGridRow[] } {
  const loaded = ensureLoaded(metaData, forecastWeekFrom);
  const producers = new Set(cellChanges.map((c) => c.data.producer_type));
  if (producers.size !== 1) {
    throw new Error(`Cell edits span producers: ${[...producers].join(", ")}`);
  }
  const [producer] = producers;
  const frame = buildFrame(rows, producer, loaded.metaData);

  // Each edit lands on the weeks as the ones before it left them, so a later
  // period spread scales what an earlier edit wrote. Every refused cell is
  // collected, so the planner can fix them all at once.
  const touched = new Set<number>();
  const refusals: CellRefusal[] = [];
  for (const cellChange of cellChanges) {
    try {
      for (const [index, value] of checkedEdits(frame, cellChange, loaded)) {
        frame.weeks[index][cellChange.data.metric_name] = value;
        touched.add(index);
      }
    } catch (error) {
      if (!(error instanceof Refusal)) throw error;
      refusals.push({ change: cellChange, reason: error.message });
    }
  }
  if (refusals.length > 0) throw new CellEditsRefused(refusals);
  const triggers = [...new Set(cellChanges.map((c) => c.data.metric_name))];

  // Mirrors the server adapter's `EOP_UNITS in cf.columns` check: frames
  // loaded without inventory metrics skip the coupled/stock passes.
  const inventory = frame.baseRows.some(
    (row) => rowString(row, "metric_name") === EOP_UNITS
  );
  if (inventory) ensurePortable(frame.baseRows, frame.slots, triggers);

  const updated = applyWeeklyKpiChanges({
    weeks: frame.weeks,
    triggers,
    fromIndex: Math.min(...touched),
    inventory,
    markdownStatus,
    // POTB's scan origin: cumulative from the plan's forecast boundary, which
    // the server also scans from — not the edited week, and not the
    // viewer-scoped `editable_week_from`.
    potbFromIndex: inventory
      ? potbOriginIndex(frame.slots, loaded.metaData, loaded.forecastWeekFrom)
      : undefined
  });
  const update = writeBack(frame, updated, loaded.metaData);
  ensureRatiosShown(update, cellChanges, loaded.metaData);
  return { update };
}

/**
 * Refuses a price or percentage the grid cannot show: it re-derives from the
 * units under it, so over none it reads 0 whatever was typed.
 */
function ensureRatiosShown(
  update: TemporalGridRow[],
  cellChanges: TemporalCellChange[],
  metaData: ForecastMetaDataType
): void {
  // A cell pasted twice shows its last value.
  const lastByCell = new Map(
    cellChanges.map((change) => [
      `${change.data.fiscal_year_name}|${change.data.metric_name}|${change.colId}`,
      change
    ])
  );
  const refusals: CellRefusal[] = [];
  for (const change of lastByCell.values()) {
    const { fiscal_year_name: year, metric_name: metric } = change.data;
    if (!isRatio(metric, metaData)) continue;
    const row = update.find(
      (candidate) =>
        rowString(candidate, "fiscal_year_name") === year &&
        rowString(candidate, "metric_name") === metric
    );
    const shown = row?.[change.colId];
    // Over no units the ratio re-derives to 0 (or blank), whatever was typed.
    const lost =
      typedValue(change.value) !== 0 &&
      (typeof shown !== "number" || shown === 0);
    if (lost) {
      refusals.push({ change, reason: "there are no units under it to price" });
    }
  }
  if (refusals.length > 0) throw new CellEditsRefused(refusals);
}

/**
 * Client-side recalculation after the intake toggle swapped one source's
 * intake rows for another's, per producer (`recalc_intake_kpis`). Rolls from
 * the forecast boundary on the swapped-in opening stock.
 */
export function recomputeIntakeLocally(
  rows: TemporalGridRow[],
  producer: string,
  metaData: ForecastMetaDataType | undefined,
  markdownStatus?: string[] | null,
  forecastWeekFrom?: number
): { update: TemporalGridRow[] } {
  const loaded = ensureLoaded(metaData, forecastWeekFrom);
  const frame = buildFrame(rows, producer, loaded.metaData);
  const boundary = firstOpenIndex(
    frame.slots,
    loaded.metaData,
    loaded.forecastWeekFrom
  );
  // A frame cut to closed weeks has nothing to roll.
  if (boundary === -1) return { update: [] };
  ensurePortable(frame.baseRows, frame.slots, [], SWAP_FLOW_INPUT_METRICS);
  const updated = applyIntakeSwap({
    weeks: frame.weeks,
    fromIndex: boundary,
    markdownStatus,
    potbFromIndex: boundary
  });
  return { update: writeBack(frame, updated, loaded.metaData) };
}

/**
 * Client-side recalculation after the OTB page's intake toggle, per
 * (collection_type, product_category, producer).
 * OTB rows carry intake totals without their legs and no FPOS, so the totals
 * stay as swapped in, and the periods roll up as the OTB load's `cf_to_gf`
 * does: no re-derived ratios, no TTL.
 */
export function recomputeOtbIntakeLocally(
  rows: TemporalGridRow[],
  metaData: ForecastMetaDataType | undefined,
  forecastWeekFrom?: number
): { update: TemporalGridRow[] } {
  const loaded = ensureLoaded(metaData, forecastWeekFrom);
  const groups = new Map<string, TemporalGridRow[]>();
  for (const row of rows) {
    const key = JSON.stringify(
      ["collection_type", "product_category", "producer_type"].map((field) =>
        rowString(row, field)
      )
    );
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  const update = [...groups.values()].flatMap(function recompute(groupRows) {
    const frame = buildFrame(
      groupRows,
      rowString(groupRows[0], "producer_type"),
      loaded.metaData
    );
    const boundary = firstOpenIndex(
      frame.slots,
      loaded.metaData,
      loaded.forecastWeekFrom
    );
    if (boundary === -1) return [];
    ensurePortable(frame.baseRows, frame.slots, [], SWAP_FLOW_INPUT_METRICS);
    const updated = applyIntakeSwap({
      weeks: frame.weeks,
      fromIndex: boundary,
      potbFromIndex: boundary,
      deriveTotals: false
    });
    return writeBack(frame, updated, loaded.metaData, {
      deriveMetrics: false,
      addTotal: false
    });
  });
  return { update };
}

/**
 * Client-side fill-in of OTB (`repl_from_otb`): each open week's POTB — the
 * buy that holds OTB at zero, stock never un-bought — added to replenishment
 * intake, then cascaded as a paste of those cells. Nothing to fill is no update.
 */
export function fillInOtbLocally(
  rows: TemporalGridRow[],
  producer: string,
  metaData: ForecastMetaDataType | undefined,
  markdownStatus?: string[] | null,
  forecastWeekFrom?: number
): { update: TemporalGridRow[] } {
  const loaded = ensureLoaded(metaData, forecastWeekFrom);
  const frame = buildFrame(rows, producer, loaded.metaData);
  const boundary = firstOpenIndex(
    frame.slots,
    loaded.metaData,
    loaded.forecastWeekFrom
  );
  if (boundary === -1) return { update: [] };
  const changes: TemporalCellChange[] = [];
  for (const [otb, replenishment] of [
    ["OPEN_TO_BUY_UNITS", "INTAKE_UNITS_REPLENISHMENT"],
    ["OPEN_TO_BUY_AT_COST", "INTAKE_AT_COST_REPLENISHMENT"]
  ]) {
    const buys = potbFromOtb(
      frame.weeks.map((week) => week[otb] ?? Number.NaN),
      boundary
    );
    frame.slots.forEach(function toChange(slot, index) {
      // A week with no OTB to fill buys nothing (the server's NaN is 0).
      const buy = Number.isNaN(buys[index]) ? 0 : buys[index];
      if (index < boundary || buy === 0) return;
      const current = frame.weeks[index][replenishment];
      // A blank leg buys from zero.
      const base = Number.isFinite(current) ? current : 0;
      changes.push({
        data: {
          metric_name: replenishment,
          fiscal_year_name: slot.fiscalYear,
          producer_type: producer
        },
        colId: slot.weekKey,
        oldValue: current,
        value: base + buy
      });
    });
  }
  if (changes.length === 0) return { update: [] };
  return applyCellChangesLocally(
    rows,
    changes,
    loaded.metaData,
    markdownStatus,
    loaded.forecastWeekFrom
  );
}

/** One cell a batch cannot apply, and why, in the planner's words. */
export interface CellRefusal {
  change: TemporalCellChange;
  reason: string;
}

/** A batch with refused cells; it applies none of them. */
export class CellEditsRefused extends Error {
  constructor(readonly refusals: CellRefusal[]) {
    super(
      refusals
        .map(
          ({ change, reason }) =>
            `${change.data.fiscal_year_name} ${change.colId} ${change.data.metric_name}: ${reason}`
        )
        .join("\n")
    );
    this.name = "CellEditsRefused";
  }
}

/** A cell the planner can correct: thrown per cell, collected per batch. */
class Refusal extends Error {}

/** The weeks one cell change writes, refusing what the plan cannot hold. */
function checkedEdits(
  frame: Frame,
  cellChange: TemporalCellChange,
  loaded: { metaData: ForecastMetaDataType; forecastWeekFrom: number }
): Map<number, number> {
  const trigger = cellChange.data.metric_name;
  if (!isWeeklyTrigger(trigger)) throw new Refusal("this KPI can't be edited");
  const typed = typedValue(cellChange.value);
  if ((trigger === FPOS || trigger === FPOS_AT_COST) && typed < 0) {
    throw new Refusal(`can't be negative (${typed})`);
  }
  const edits = editedWeeks(
    frame.slots,
    frame.weeks,
    cellChange,
    loaded.metaData
  );
  ensureOpen(
    frame.slots,
    [...edits.keys()],
    loaded.metaData,
    loaded.forecastWeekFrom
  );
  return edits;
}

/**
 * The typed value, a cleared cell as 0. The value parser hands on what it
 * cannot read as the raw text, so anything but a number is refused.
 */
function typedValue(raw: unknown): number {
  if (raw == null || raw === "") return 0;
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    throw new Refusal(`"${String(raw)}" is not a number the grid can read`);
  }
  return raw;
}

/** Throws unless the grid's metadata and forecast boundary have loaded. */
function ensureLoaded(
  metaData: ForecastMetaDataType | undefined,
  forecastWeekFrom: number | undefined
): { metaData: ForecastMetaDataType; forecastWeekFrom: number } {
  if (!metaData?.weekPeriodMapping || forecastWeekFrom === undefined) {
    throw new Error("Grid recompute before the grid's metadata has loaded");
  }
  return { metaData, forecastWeekFrom };
}

interface Frame {
  baseRows: TemporalGridRow[];
  slots: WeekSlot[];
  weeks: MetricValues[];
}

/**
 * One producer's rows as the engine's weekly frame (the server's
 * .xs(producer)), without the client-computed OPTION_* placeholder rows the
 * server adapter filters out too.
 */
function buildFrame(
  rows: TemporalGridRow[],
  producer: string,
  metaData: ForecastMetaDataType
): Frame {
  const baseRows = rows.filter(
    (row) =>
      rowString(row, "producer_type") === producer &&
      !rowString(row, "metric_name").startsWith("OPTION_")
  );
  const slots = buildSlots(baseRows, metaData.yearMapping);
  const weeks: MetricValues[] = slots.map(function toWeek(slot) {
    const week: MetricValues = {};
    for (const row of baseRows) {
      if (rowString(row, "fiscal_year_name") !== slot.fiscalYear) continue;
      const value = row[slot.weekKey];
      // Missing/null cells are NaN, matching the server pivot (load_gf):
      // the payload drops nulls and pandas reads the holes as NaN — e.g.
      // target stock beyond the look-ahead horizon must stay NaN, not 0.
      week[rowString(row, "metric_name")] =
        typeof value === "number" ? value : Number.NaN;
    }
    return week;
  });
  return { baseRows, slots, weeks };
}

/**
 * The frame's rows with the recomputed weeks written in and rolled up into the
 * M/Q/HY/PTG/TTL columns (server: sf_to_gf).
 */
function writeBack(
  frame: Frame,
  updated: MetricValues[],
  metaData: ForecastMetaDataType,
  rollUp: RollUpOptions = { deriveMetrics: true, addTotal: true }
): TemporalGridRow[] {
  // The sales ratios re-derive from the weekly inputs, as sf_to_gf and every
  // reload do: a price the cascade held over a week with no units is not
  // shown, since a save could not keep it. Week cover and its rate-of-sales
  // denominator are grid-layer metrics too.
  if (rollUp.deriveMetrics) {
    updated.forEach(function rederive(week, index) {
      updated[index] = recalcDerivedMetrics(week, WEEKLY_SALES_DERIVATIONS);
    });
    fillWeeklyWeekCover(updated);
  }

  const updatedRows = frame.baseRows.map(function clone(row) {
    return { ...row };
  });
  const rowByKey = new Map(
    updatedRows.map((row) => [
      `${rowString(row, "fiscal_year_name")}_${rowString(row, "metric_name")}`,
      row
    ])
  );
  frame.slots.forEach(function writeWeek(slot, index) {
    for (const [metric, value] of Object.entries(updated[index])) {
      const row = rowByKey.get(`${slot.fiscalYear}_${metric}`);
      if (row) {
        // Non-finite results (NaN, ±Infinity) have no JSON representation;
        // the server serialises them to null, rendered as blank.
        row[slot.weekKey] = Number.isFinite(value) ? value : null;
      }
    }
  });
  rollUpTemporalAggregates(frame.slots, updated, rowByKey, metaData, rollUp);
  return updatedRows;
}

/** sf_to_gf's `calculate_derived_metrics` and `add_total` switches. */
interface RollUpOptions {
  /** Re-derive ratios (and week cover) from the period aggregates. */
  deriveMetrics: boolean;
  /** Write the TTL column. */
  addTotal: boolean;
}

/**
 * The new value of each slot an edit writes, keyed by slot index: the one week
 * of a weekly cell, the last week of a `last` period cell, or every week of any
 * other period cell, spread as the server's `roll_down_value` spreads it. A
 * cleared cell writes 0.
 */
function editedWeeks(
  slots: WeekSlot[],
  weeks: MetricValues[],
  cellChange: TemporalCellChange,
  metaData: ForecastMetaDataType
): Map<number, number> {
  const { fiscal_year_name: fiscalYear, metric_name: trigger } =
    cellChange.data;
  const newValue = typedValue(cellChange.value);
  const periodWeeks = backingWeeks(metaData, fiscalYear, cellChange.colId);
  if (periodWeeks.length === 0) {
    throw new Refusal("the grid has no such period");
  }
  const indices = periodWeeks.map(function slotOf(weekKey) {
    const index = slots.findIndex(
      (slot) => slot.fiscalYear === fiscalYear && slot.weekKey === weekKey
    );
    if (index === -1) throw new Refusal(`the grid holds no ${weekKey}`);
    return index;
  });
  if (indices.length === 1) return new Map([[indices[0], newValue]]);

  const rule = temporalAggRule(trigger, metaData.metricTemporalAgg);
  // A `last` period is its last week: the edit writes that week alone, and
  // the earlier weeks keep what they hold.
  if (rule === "last" && !spreadsEveryWeek(trigger))
    return new Map([[indices[indices.length - 1], newValue]]);

  const values = indices.map((index) => weeks[index][trigger] ?? Number.NaN);
  const oldValue = numberOrZero(cellChange.oldValue, "old value");
  // A price or percentage period re-derives from its held denominator, so
  // only scaling every week by new / old shows the new value there.
  if (isRatio(trigger, metaData) && oldValue !== 0) {
    return new Map(
      indices.map((index, i) => [index, (values[i] * newValue) / oldValue])
    );
  }
  // Each week moves by its share of the change, so a mixed-sign or near-zero
  // period keeps its shape.
  const scale = aggregate(values.map(Math.abs), rule);
  if (!Number.isFinite(scale) || scale === 0) {
    // Nothing to share by: a sum splits evenly; any other holds it in every week.
    const fromNothing = rule === "sum" ? newValue / indices.length : newValue;
    return new Map(indices.map((index) => [index, fromNothing]));
  }
  const change = newValue - oldValue;
  return new Map(
    indices.map((index, i) => [
      index,
      values[i] + (change * Math.abs(values[i])) / scale
    ])
  );
}

/** A price or a percentage (`is_ratio_metric`): its periods re-derive. */
function isRatio(metric: string, metaData: ForecastMetaDataType): boolean {
  return (
    metaData.metricCategoryMapping[metric] === "Price" ||
    isPercentageMetric(metric, metaData.metricFormatMapping)
  );
}

/** A cell value as a number, blank as 0; throws on anything else. */
function numberOrZero(raw: unknown, label: string): number {
  if (raw == null || raw === "") return 0;
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    throw new Error(`Cell edit with a non-numeric ${label}: ${String(raw)}`);
  }
  return value;
}

/** Throws when an edit reaches a week before the forecast boundary. */
function ensureOpen(
  slots: WeekSlot[],
  indices: number[],
  metaData: ForecastMetaDataType,
  forecastWeekFrom: number
): void {
  for (const index of indices) {
    const { fiscalYear, weekKey } = slots[index];
    const yearWeek =
      metaData.weekPeriodMapping[`${fiscalYear}${weekKey}`]?.yearWeek;
    if (yearWeek !== undefined && yearWeek < forecastWeekFrom) {
      throw new Refusal(`${fiscalYear} ${weekKey} is closed: it holds actuals`);
    }
  }
}

/** The flow inputs an intake swap reads: it rolls stock and fills OTB, never TS from FPOS. */
const SWAP_FLOW_INPUT_METRICS = WEEKLY_FLOW_INPUT_METRICS.filter(
  (metric) => metric !== FPOS && metric !== FPOS_AT_COST
);

/**
 * Throws when the inventory frame lacks what the stock passes read every week
 * — a flow input, or a coupled trigger's input — or, where FPOS is read,
 * holds a negative one, which `targetStockScan` would read on every edit.
 */
function ensurePortable(
  baseRows: TemporalGridRow[],
  slots: WeekSlot[],
  triggers: string[],
  flowInputs: string[] = WEEKLY_FLOW_INPUT_METRICS
): void {
  const requiredMetrics = [
    ...flowInputs,
    ...triggers.flatMap(
      (trigger) => WEEKLY_COUPLED_INPUT_METRICS[trigger] ?? []
    )
  ];
  for (const fiscalYear of new Set(slots.map((slot) => slot.fiscalYear))) {
    const metrics = new Set(
      baseRows
        .filter((row) => rowString(row, "fiscal_year_name") === fiscalYear)
        .map((row) => rowString(row, "metric_name"))
    );
    const missing = requiredMetrics.filter((metric) => !metrics.has(metric));
    if (missing.length > 0) {
      throw new Error(`${fiscalYear} lacks ${missing.join(", ")}`);
    }
  }
  for (const row of baseRows) {
    const metric = rowString(row, "metric_name");
    if (!requiredMetrics.includes(metric)) continue;
    if (metric !== FPOS && metric !== FPOS_AT_COST) continue;
    for (const weekKey of WEEKLY_KEYS) {
      const value = row[weekKey];
      if (typeof value === "number" && value < 0) {
        throw new Error(
          `Negative ${metric} in ${rowString(row, "fiscal_year_name")} ${weekKey}`
        );
      }
    }
  }
}

/** Index of the first slot at or after the forecast boundary, or -1. */
function firstOpenIndex(
  slots: WeekSlot[],
  metaData: ForecastMetaDataType,
  forecastWeekFrom: number
): number {
  return slots.findIndex(function atOrAfterBoundary(slot) {
    const periods =
      metaData.weekPeriodMapping[`${slot.fiscalYear}${slot.weekKey}`];
    return periods != null && periods.yearWeek >= forecastWeekFrom;
  });
}

/** Index of the first slot at or after the forecast boundary — POTB's scan origin. */
function potbOriginIndex(
  slots: WeekSlot[],
  metaData: ForecastMetaDataType,
  forecastWeekFrom: number
): number {
  const index = firstOpenIndex(slots, metaData, forecastWeekFrom);
  if (index === -1) {
    throw new Error(
      `No week of the grid reaches the forecast boundary ${forecastWeekFrom}`
    );
  }
  return index;
}

/**
 * Aggregate the recomputed weekly frame into the temporal columns and write
 * them onto the matching rows (keyed `fiscalYear_metric`). Throws when a
 * slot's period membership is absent: a month summed over fewer weeks would
 * be wrong.
 */
export function rollUpTemporalAggregates(
  slots: WeekSlot[],
  updated: MetricValues[],
  rowByKey: Map<string, TemporalGridRow>,
  metaData: ForecastMetaDataType,
  rollUp: RollUpOptions
): void {
  const aggWeeks: AggWeek[] = slots.map(function toAggWeek(slot, index) {
    const periods =
      metaData.weekPeriodMapping[`${slot.fiscalYear}${slot.weekKey}`];
    if (!periods) {
      throw new Error(
        `${slot.fiscalYear} ${slot.weekKey} has no period membership`
      );
    }
    return { fiscalYear: slot.fiscalYear, values: updated[index], periods };
  });

  const aggregates = computeTemporalAggregates(
    aggWeeks,
    metaData.metricTemporalAgg,
    rollUp.deriveMetrics ? undefined : []
  );
  for (const [fiscalYear, byPeriod] of Object.entries(aggregates)) {
    for (const [period, metricValues] of Object.entries(byPeriod)) {
      if (period === TOTAL_LABEL && !rollUp.addTotal) continue;
      for (const [metric, value] of Object.entries(metricValues)) {
        const row = rowByKey.get(`${fiscalYear}_${metric}`);
        // Non-finite results (NaN, ±Infinity) have no JSON representation;
        // the server serialises them to null, rendered as blank.
        if (row) row[period] = Number.isFinite(value) ? value : null;
      }
    }
  }
}
